<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="primary"
          icon="mdi-newspaper-variant"
          inline
          class="ml-0 px-5 py-4"
          :title="addMode ? 'Nova Release' : `Editar Release v${form.versao}`"
        >
          <v-form ref="form" class="mt-6" @submit.prevent="salvar('rascunho')">
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.versao"
                  label="Versão *"
                  placeholder="ex: 1.5.0"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.titulo"
                  label="Título (opcional)"
                  placeholder="ex: Grande atualização de mapas"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-menu v-model="menuData" :close-on-content-click="false" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="dataLancamentoFormatada"
                      label="Data de Lançamento *"
                      prepend-inner-icon="mdi-calendar"
                      :rules="[rules.required]"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="form.data_lancamento" locale="pt-BR" no-title @input="menuData = false" />
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.empresas"
                  :items="empresas"
                  item-text="name"
                  item-value="id"
                  label="Empresas destinatárias (vazio = todas)"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  :loading="loadingEmpresas"
                />
              </v-col>
            </v-row>

            <v-divider class="mb-6 mt-2" />

            <div class="align-center d-flex mb-4">
              <span class="font-weight-bold subtitle-1">Itens do Changelog</span>
              <v-spacer />
              <v-btn color="primary" small outlined @click="adicionarItem">
                <v-icon left small>mdi-plus</v-icon>
                Adicionar Item
              </v-btn>
            </div>

            <v-alert v-if="form.itens.length === 0" type="info" text dense>Adicione ao menos um item ao changelog.</v-alert>

            <v-card v-for="(item, idx) in form.itens" :key="idx" outlined class="mb-3 pa-4">
              <div class="align-center d-flex mb-3">
                <v-chip :color="tipoConfig(item.tipo).color" small dark class="mr-2">
                  <v-icon left x-small>{{ tipoConfig(item.tipo).icon }}</v-icon>
                  {{ tipoConfig(item.tipo).label }}
                </v-chip>
                <v-spacer />
                <v-btn icon x-small color="grey" class="mr-1" :disabled="idx === 0" @click="moverItem(idx, -1)">
                  <v-icon x-small>mdi-arrow-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  color="grey"
                  class="mr-2"
                  :disabled="idx === form.itens.length - 1"
                  @click="moverItem(idx, 1)"
                >
                  <v-icon x-small>mdi-arrow-down</v-icon>
                </v-btn>
                <v-btn icon x-small color="red" @click="removerItem(idx)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="item.tipo"
                    :items="tiposOpcoes"
                    item-text="label"
                    item-value="value"
                    label="Tipo *"
                    :rules="[rules.required]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="9">
                  <v-textarea
                    v-model="item.descricao"
                    label="Descrição *"
                    :rules="[rules.required]"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                  />
                </v-col>
              </v-row>
            </v-card>

            <v-divider class="mb-6 mt-4" />

            <div class="d-flex justify-end" style="gap: 12px">
              <v-btn outlined @click="$router.push('/releases-novidades/gerenciar')">Cancelar</v-btn>
              <v-btn outlined color="secondary" :loading="saving" @click="salvar('rascunho')">
                <v-icon left>mdi-content-save-outline</v-icon>
                Salvar Rascunho
              </v-btn>
              <v-btn color="primary" :loading="saving" @click="salvar('publicado')">
                <v-icon left>mdi-publish</v-icon>
                Publicar
              </v-btn>
            </div>
          </v-form>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import ReleasesNovidadesService from '@/services/ReleasesNovidadesService';
  import CompanyService from '@/services/CompanyService';
  import routes from '@/store/modules/routes';

  const TIPOS = [
    { value: 'nova_funcionalidade', label: 'Nova Funcionalidade', icon: 'mdi-star', color: 'success' },
    { value: 'melhoria', label: 'Melhoria', icon: 'mdi-arrow-up-bold', color: 'info' },
    { value: 'correcao', label: 'Correção', icon: 'mdi-wrench', color: 'error' },
    { value: 'mudanca_importante', label: 'Mudança Importante', icon: 'mdi-alert', color: 'warning' }
  ];

  export default {
    name: 'ReleasesNovidadesEdit',

    data: () => ({
      form: {
        id: null,
        versao: '',
        titulo: '',
        data_lancamento: '',
        empresas: [],
        itens: []
      },
      empresas: [],
      menuData: false,
      loading: false,
      loadingEmpresas: false,
      saving: false,
      tiposOpcoes: TIPOS,
      rules: { required: (v) => !!v || 'Campo obrigatório' }
    }),

    computed: {
      addMode() {
        return !this.$route.params.id;
      },

      dataLancamentoFormatada() {
        if (!this.form.data_lancamento) return '';
        const [y, m, d] = this.form.data_lancamento.split('-');
        return `${d}/${m}/${y}`;
      }
    },

    mounted() {
      this.getEmpresas();
      if (!this.addMode) this.getRelease(this.$route.params.id);
    },

    methods: {
      getEmpresas() {
        this.loadingEmpresas = true;
        CompanyService.getAll()
          .then(({ data }) => (this.empresas = data.data))
          .catch(() => this.$toast.error('Erro ao carregar empresas.', '', { position: 'topRight' }))
          .finally(() => (this.loadingEmpresas = false));
      },

      getRelease(id) {
        this.loading = true;
        ReleasesNovidadesService.getReleaseById(id)
          .then(({ data }) => {
            const r = data.data;
            this.form = {
              id: r.id,
              versao: r.versao,
              titulo: r.titulo || '',
              data_lancamento: r.data_lancamento,
              empresas: r.empresas,
              itens: r.itens
            };
          })
          .catch(() => this.$toast.error('Erro ao carregar a release.', '', { position: 'topRight' }))
          .finally(() => (this.loading = false));
      },

      adicionarItem() {
        this.form.itens.push({ tipo: 'nova_funcionalidade', descricao: '' });
      },

      removerItem(idx) {
        this.form.itens.splice(idx, 1);
      },

      moverItem(idx, direcao) {
        const destino = idx + direcao;
        const itens = [...this.form.itens];
        [itens[idx], itens[destino]] = [itens[destino], itens[idx]];
        this.form.itens = itens;
      },

      tipoConfig(valor) {
        return TIPOS.find((t) => t.value === valor) || TIPOS[0];
      },

      salvar(status) {
        if (!this.$refs.form.validate()) return;
        if (this.form.itens.length === 0) {
          this.$toast.warning('Adicione ao menos um item ao changelog.', '', { position: 'topRight' });
          return;
        }

        this.saving = true;
        ReleasesNovidadesService.save({ ...this.form, status })
          .then(() => {
            this.$toast.success('Release salva com sucesso.', '', { position: 'topRight' });
            this.$router.push({ name: routes.state.appRoutes['RELEASES_NOVIDADES_GERENCIAR'] });
          })
          .catch(() => this.$toast.error('Erro ao salvar a release.', '', { position: 'topRight' }))
          .finally(() => (this.saving = false));
      }
    }
  };
</script>
